import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Organization {
    _id: string;
    name: string;
    slug: string;
    customSlug?: string;
    logoUrl?: string;
    stytchOrganizationId: string;
    settings?: {
        allowedEmailDomains?: string[];
        ssoEnabled?: boolean;
    };
}

export interface OrganizationMember {
    _id: string;
    email: string;
    displayName?: string;
    organizationRole?: 'owner' | 'admin' | 'member';
    profilePicture?: string;
    status?: 'active' | 'pending'; // Stytch member status
    stytchMemberId?: string;
    hasLoggedIn?: boolean;
}

export interface DiscoveredOrganization {
    organization_id: string;
    organization_name: string;
    organization_logo_url?: string; // Optional company logo URL
    member_id: string;
    membership: {
        type: string;
        details: any;
    };
}

interface OrganizationState {
    currentOrganization: Organization | null;
    members: OrganizationMember[];
    discoveredOrganizations: DiscoveredOrganization[];
    intermediateSessionToken: string | null;
    loading: boolean;
    error: string | null;
}

const initialState: OrganizationState = {
    currentOrganization: null,
    members: [],
    discoveredOrganizations: [],
    intermediateSessionToken: null,
    loading: false,
    error: null
};

const organizationSlice = createSlice({
    name: 'organization',
    initialState,
    reducers: {
        setCurrentOrganization: (state, action: PayloadAction<Organization | null>) => {
            state.currentOrganization = action.payload;
        },
        setMembers: (state, action: PayloadAction<OrganizationMember[]>) => {
            state.members = action.payload;
        },
        updateMember: (state, action: PayloadAction<OrganizationMember>) => {
            const index = state.members.findIndex(m => m._id === action.payload._id);
            if (index !== -1) {
                state.members[index] = action.payload;
            }
        },
        removeMember: (state, action: PayloadAction<string>) => {
            state.members = state.members.filter(m => m._id !== action.payload);
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setDiscoveredOrganizations: (state, action: PayloadAction<DiscoveredOrganization[]>) => {
            state.discoveredOrganizations = action.payload;
        },
        setIntermediateSessionToken: (state, action: PayloadAction<string | null>) => {
            state.intermediateSessionToken = action.payload;
        },
        clearDiscoveryData: (state) => {
            state.discoveredOrganizations = [];
            state.intermediateSessionToken = null;
        },
        clearOrganization: (state) => {
            state.currentOrganization = null;
            state.members = [];
            state.discoveredOrganizations = [];
            state.intermediateSessionToken = null;
            state.error = null;
        }
    }
});

export const {
    setCurrentOrganization,
    setMembers,
    updateMember,
    removeMember,
    setLoading,
    setError,
    setDiscoveredOrganizations,
    setIntermediateSessionToken,
    clearDiscoveryData,
    clearOrganization
} = organizationSlice.actions;

export default organizationSlice.reducer;
